import React, { useState, useEffect } from "react";
import useIsMobile from "../hooks/useIsMobile";
import { db, auth } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { Lock, Rocket } from "lucide-react"; // Added for gatekeep

/* WEB COMPONENTS */
import AddAccountContainer from "./AddAccountContainer";
import AccountsList from "./AccountsList";
import AddTrade from "./AddTrade";
import TradeJournal from "./TradeJournal";
import AccountDashboard from "./AccountDashboard";
import Support from "./support";
import LotCalculate from "./LotCalculate";
import Newsandevents from "./Newsandevents";
import SubscriptionPlan from "./SubscriptionPlan"; 
import DashboardLayout from "../layout/DashboardLayout";
import Sidebar from "../layout/Sidebar";
import Header from "../layout/Header";

/* MOBILE */
import MobileDashboard from "../mobile/MobileDashboard";

// NEW: GATEKEEP OVERLAY
const LockedOverlay = ({ setTab }) => (
  <div style={styles.lockOverlay}>
    <div style={styles.blurContainer}>
      <div style={{ opacity: 0.05, filter: 'blur(10px)', pointerEvents: 'none' }}>
        <AccountDashboard />
      </div>
    </div>
    <div style={styles.lockCard}>
      <div style={styles.lockIconCircle}>
        <Lock size={32} color="#38bdf8" />
      </div>
      <h2 style={styles.lockTitle}>Premium Feature</h2>
      <p style={styles.lockText}>
        This tool is exclusive to <b>Profitable Trader Plan</b> members. 
        Unlock professional analytics and advanced risk tools.
      </p>
      <button onClick={() => setTab("subscription")} style={styles.unlockBtn}>
        <Rocket size={18} style={{ marginRight: 8 }} />
        Join the Family
      </button>
    </div>
  </div>
);

export default function Dashboard() {
  const [tab, setTab] = useState("accounts");
  const [userData, setUserData] = useState({ plan: "free", expiry: null });
  const isMobile = useIsMobile();

  // Listen for membership status and expiry in real-time
  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      const unsub = onSnapshot(doc(db, "users", user.uid), (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setUserData({
            plan: data?.plan || "free",
            expiry: data?.subscriptionExpiryDate || null
          });
        }
      });
      return () => unsub();
    }
  }, []);

  if (isMobile) {
    return <MobileDashboard />;
  }

  // Check if plan is "profitable" AND not expired
  const checkMembershipStatus = () => {
    if (userData.plan !== "profitable") return false;
    if (!userData.expiry) return false;

    const now = new Date();
    const expiry = new Date(userData.expiry);
    return now < expiry; // Returns true only if current time is before expiry
  };

  const hasMembership = checkMembershipStatus();

  // Logic helper to keep original tab rendering but add overlay
  const renderGated = (Component) => {
    return hasMembership ? Component : <LockedOverlay setTab={setTab} />;
  };

  return (
    <>
      <style>{`
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          background-color: #020617;
          overflow: hidden;
          width: 100%;
          height: 100%;
        }
        *::-webkit-scrollbar { display: none !important; }
        * { -ms-overflow-style: none !important; scrollbar-width: none !important; }
      `}</style>

      <DashboardLayout
        sidebar={<Sidebar tab={tab} setTab={setTab} hasMembership={hasMembership} />}
        header={<Header activeTab={tab} userPlan={userData.plan} hasMembership={hasMembership} userData={userData} />}
      >
        {/* PUBLIC ACCESSIBLE TABS */}
        {tab === "accounts" && (
          <>
            <AddAccountContainer />
            <AccountsList />
          </>
        )}
        {tab === "addTrade" && renderGated(<AddTrade />)}
        {tab === "journal" && renderGated(<TradeJournal />)}
        {tab === "support" && <Support />}
        {tab === "subscription" && <SubscriptionPlan />}

        {/* GATED ACCESSIBLE TABS */}
        {tab === "dashboard" && renderGated(<AccountDashboard />)}
        {tab === "lotCalculator" && renderGated(<LotCalculate />)}
        {tab === "news" && renderGated(<Newsandevents />)}
      </DashboardLayout>
    </>
  );
}

// Added styles for the gatekeep logic
const styles = {
  lockOverlay: { position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  blurContainer: { position: 'absolute', inset: 0, zIndex: 0 },
  lockCard: { position: 'relative', zIndex: 1, background: '#0f172a', padding: '48px 32px', borderRadius: '28px', border: '1px solid rgba(56, 189, 248, 0.2)', textAlign: 'center', maxWidth: '420px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)' },
  lockIconCircle: { width: '72px', height: '72px', background: 'rgba(56, 189, 248, 0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' },
  lockTitle: { fontSize: '24px', fontWeight: '800', color: '#f8fafc', marginBottom: '16px' },
  lockText: { fontSize: '15px', color: '#94a3b8', lineHeight: '1.6', marginBottom: '32px' },
  unlockBtn: { width: '100%', background: '#38bdf8', color: '#020617', border: 'none', padding: '16px', borderRadius: '12px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }
};